import React, { useState, useEffect } from 'react';
import { tablesAPI } from '../services/api';
import { Save, Search, Plus, Trash2, Edit2, X, Check, Building2, MapPin } from 'lucide-react';

const Registre = () => {
  const [activeTab, setActiveTab] = useState('caisses');
  const [caisses, setCaisses] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newCaisse, setNewCaisse] = useState({
    nom: '',
    telephone: '',
    email: '',
    site_web: '',
    horaires: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'caisses') {
        const res = await tablesAPI.getCaisses();
        setCaisses(res.data);
      } else {
        const res = await tablesAPI.getDepartements();
        setDepartements(res.data);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({ ...item });
    setIsAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async () => {
    try {
      if (activeTab === 'caisses') {
        await tablesAPI.updateCaisse(editingId, editData);
      } else {
        await tablesAPI.updateDepartement(editingId, editData);
      }
      await loadData();
      cancelEdit();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleNewCaisseChange = (field, value) => {
    setNewCaisse(prev => ({ ...prev, [field]: value }));
  };

  const addCaisse = async () => {
    if (!newCaisse.nom.trim()) {
      alert('Le nom de la caisse est obligatoire');
      return;
    }
    try {
      await tablesAPI.createCaisse(newCaisse);
      await loadData();
      setIsAdding(false);
      setNewCaisse({ nom: '', telephone: '', email: '', site_web: '', horaires: '' });
    } catch (error) {
      console.error('Erreur création:', error);
      alert('Erreur lors de la création');
    }
  };

  const deleteCaisse = async (id) => {
    if (!window.confirm('Supprimer cette caisse complémentaire ?')) return;
    try {
      await tablesAPI.deleteCaisse(id);
      await loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredData = activeTab === 'caisses' 
    ? caisses.filter(c => c.nom?.toLowerCase().includes(search.toLowerCase()))
    : departements.filter(d => 
        d.nom?.toLowerCase().includes(search.toLowerCase()) || 
        d.code?.includes(search)
      );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 Registre</h1>
          <p className="text-gray-600 mt-1">Caisses de retraite complémentaire et départements</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => { setActiveTab('caisses'); setIsAdding(false); cancelEdit(); }}
          className={`flex items-center gap-2 px-6 py-3 font-semibold transition border-b-2 ${
            activeTab === 'caisses'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Building2 className="w-5 h-5" />
          Caisses Complémentaires ({caisses.length})
        </button>
        <button
          onClick={() => { setActiveTab('departements'); setIsAdding(false); cancelEdit(); }}
          className={`flex items-center gap-2 px-6 py-3 font-semibold transition border-b-2 ${
            activeTab === 'departements'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <MapPin className="w-5 h-5" />
          Départements ({departements.length})
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Header avec recherche et bouton ajouter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          {activeTab === 'caisses' && (
            <button
              onClick={() => { setIsAdding(true); cancelEdit(); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              disabled={isAdding}
            >
              <Plus className="w-5 h-5" />
              Ajouter une caisse
            </button>
          )}
        </div>

        {/* Formulaire d'ajout de caisse */}
        {isAdding && activeTab === 'caisses' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nouvelle caisse complémentaire
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={newCaisse.nom}
                  onChange={(e) => handleNewCaisseChange('nom', e.target.value)}
                  placeholder="Ex: AG2R La Mondiale"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="text"
                  value={newCaisse.telephone}
                  onChange={(e) => handleNewCaisseChange('telephone', e.target.value)}
                  placeholder="01 XX XX XX XX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newCaisse.email}
                  onChange={(e) => handleNewCaisseChange('email', e.target.value)}
                  placeholder="contact@caisse.fr"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                <input
                  type="url"
                  value={newCaisse.site_web}
                  onChange={(e) => handleNewCaisseChange('site_web', e.target.value)}
                  placeholder="https://www.caisse.fr"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Horaires</label>
                <input
                  type="text"
                  value={newCaisse.horaires}
                  onChange={(e) => handleNewCaisseChange('horaires', e.target.value)}
                  placeholder="Lun-Ven 9h-17h"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setIsAdding(false); setNewCaisse({ nom: '', telephone: '', email: '', site_web: '', horaires: '' }); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={addCaisse}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Check className="w-4 h-4" />
                Ajouter
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {activeTab === 'caisses' ? (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Téléphone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Site web</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Horaires</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((caisse) => (
                  <tr key={caisse.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {editingId === caisse.id ? (
                      <>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editData.nom || ''}
                            onChange={(e) => handleEditChange('nom', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editData.telephone || ''}
                            onChange={(e) => handleEditChange('telephone', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editData.email || ''}
                            onChange={(e) => handleEditChange('email', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editData.site_web || ''}
                            onChange={(e) => handleEditChange('site_web', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editData.horaires || ''}
                            onChange={(e) => handleEditChange('horaires', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={saveEdit} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Sauvegarder">
                              <Check className="w-5 h-5" />
                            </button>
                            <button onClick={cancelEdit} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="Annuler">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-4 font-medium text-gray-900">{caisse.nom}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{caisse.telephone || '-'}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{caisse.email || '-'}</td>
                        <td className="py-3 px-4 text-sm">
                          {caisse.site_web ? (
                            <a href={caisse.site_web} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              Lien
                            </a>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{caisse.horaires || '-'}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => startEdit(caisse)} 
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => deleteCaisse(caisse.id)} 
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Code</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Téléphone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 20).map((dept) => (
                  <tr key={dept.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{dept.code}</td>
                    <td className="py-3 px-4">{dept.nom}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{dept.service || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{dept.telephone || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{dept.email || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <button onClick={() => startEdit(dept)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {filteredData.length === 0 && !isAdding && (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun résultat trouvé</p>
            {activeTab === 'caisses' && (
              <button
                onClick={() => setIsAdding(true)}
                className="mt-2 text-green-600 hover:text-green-700 font-medium"
              >
                + Ajouter une caisse complémentaire
              </button>
            )}
          </div>
        )}

        {activeTab === 'departements' && filteredData.length > 20 && (
          <div className="mt-4 text-center text-sm text-gray-500">
            Affichage de 20 sur {filteredData.length} départements
          </div>
        )}
      </div>
    </div>
  );
};

export default Registre;
